// ENG-3451: account-collision page.
//
// Shown when Auth0 rejects a sign-in because the email already belongs to an
// account on a different connection (Google vs Username-Password-
// Authentication). Driven ONLY by the error_description query param matching
// the verified `COLLISION:<connection>` prefix (see auth/collision.ts):
//   - known connection   → targeted guidance naming the friendly method,
//   - unknown connection → fixed safe generic collision guidance,
//   - no collision signal → fixed safe generic sign-in problem message.
// The raw error_description is NEVER rendered (security contract in
// auth/collision.ts) — only the mapped fixed strings below reach the DOM.
//
// Theming mirrors the Electron login screen (Centered + vendor logo + the
// design-system Button) so the page reads correctly for both the Iris and
// Hive vendor themes. Per rule: code-comments-required +
// reference-rules-in-comments (ai.knowledge/global).

import { getCollisionProviderLabel, parseCollisionConnection } from '#~/auth/collision.js';
import { isElectronApp } from '#~/electron/utils.js';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '@studio/design-system/button';
import { Centered } from '@studio/design-system/overlay';
import { getCurrentVendorLogo, vendorName } from '@studio/design-system/themes/vendor.js';
import type { ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Paths } from '../constants.js';

/** `<LoginCollision>` component — account-collision guidance page. */
export const LoginCollision = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useAuth0();

  const connection = parseCollisionConnection(searchParams.get('error_description') ?? undefined);

  // The way back to sign-in differs per surface: the Electron shell has its
  // own login screen (with the Continue-with-Google button), the web app uses
  // the hosted-redirect login route.
  //
  // Web: Auth0 keeps the browser's session from the denied Google sign-in.
  // Going straight to /login would reuse that session, skip the login page,
  // be denied again, and land back here in a loop. Ending the Auth0 session
  // first makes /login show the real login page. Not the /logout route: that
  // records a sign-out and resets analytics for a user who never signed in.
  const handleTryAgain = (): void => {
    if (isElectronApp()) {
      navigate('/electron/login');
      return;
    }

    void logout({
      logoutParams: { returnTo: `${window.location.origin}/${Paths.login}` }
    });
  };

  const isCollision = connection !== null;
  const providerLabel = connection ? getCollisionProviderLabel(connection) : null;

  // Fixed copy per branch — no server-controlled content reaches the DOM.
  // The unknown-connection branch gets its own sentence so the guidance
  // stays natural without naming a method Studio doesn't recognize.
  const message = (() => {
    if (!isCollision) {
      return `Something went wrong while signing you in to ${vendorName()}. Please go back and try again.`;
    }

    if (connection === 'unknown') {
      return `You’ve signed in before using a different method. Please go back and sign in to ${vendorName()} the way you originally did.`;
    }

    return `You’ve signed in before with ${providerLabel}. Please go back and use ${providerLabel} to sign in to ${vendorName()}.`;
  })();

  return (
    <Centered>
      <div className='flex flex-col items-center' data-stag='login.collision.container'>
        <img src={getCurrentVendorLogo()} alt={vendorName()} className='w-[236px]' />
        <div className='mt-64 flex flex-col items-center gap-16 text-center'>
          {/* role="alert" so screen readers announce the outcome as soon as
           * the route mounts — the user arrived here from a failed sign-in
           * and must not land on a silent page. */}
          <div role='alert'>
            <h1 className='font-demibold' data-stag='login.collision.heading'>
              {isCollision ? 'This email already has an account' : 'We couldn’t sign you in'}
            </h1>
            <p className='px-12 text-muted' data-stag='login.collision.message'>
              {message}
            </p>
          </div>

          <div className='my-6' />

          <Button
            className='block w-full'
            data-stag='login.collision.try_again'
            size='lg'
            onClick={handleTryAgain}
          >
            Back to log in
          </Button>
        </div>
      </div>
    </Centered>
  );
};
